import { useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';

import { api } from '../../api/client.ts';
import { useSession } from '../../auth/session.tsx';
import { Button, TextInput } from '../../components/ui.tsx';
import { FormAlerts } from '../../components/FormAlerts.tsx';
import { useFormErrors } from '../../components/useFormErrors.ts';
import { checkRequired } from '../../forms.ts';
import { useLang } from '../../i18n.ts';
import { AuthLayout } from './AuthLayout.tsx';
import { NewPasswordFields, useNewPassword } from './NewPasswordFields.tsx';
import { PASSWORD_CHANGE_CODE_FIELDS } from './passwordErrors.ts';

/**
 * A temporary password (new user or admin reset) must be replaced before anything else; so must a
 * management account's password shorter than 16 characters (owner rule D147).
 */
export function ForcedPasswordChange() {
  const { t } = useLang();
  const { signOut, profile } = useSession();
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState('');
  const rule = profile?.passwordRule;
  const long = rule?.reason === 'offline_management';
  const password = useNewPassword(rule);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const errors = useFormErrors(error, {
    fields: {
      currentPassword: t('auth.currentPassword'),
      newPassword: t('auth.newPassword'),
      repeatPassword: t('auth.repeatPassword'),
    },
    codeFields: PASSWORD_CHANGE_CODE_FIELDS,
  });

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!errors.validate({ currentPassword: checkRequired(current), ...password.checks() })) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/password/change', {
        currentPassword: current,
        newPassword: password.value,
      });
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title={long ? t('auth.mustChangeLongTitle') : t('auth.mustChangeTitle')}
      subtitle={long ? t('auth.mustChangeLongIntro') : t('auth.mustChangeIntro')}
    >
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
        <FormAlerts errors={errors} />
        <TextInput
          label={t('auth.currentPassword')}
          type="password"
          autoComplete="current-password"
          dir="ltr"
          value={current}
          error={errors.errorFor('currentPassword')}
          onChange={(e) => {
            errors.touched('currentPassword');
            setCurrent(e.target.value);
          }}
          className="text-start"
        />
        <NewPasswordFields state={password} errors={errors} rule={rule} />
        <Button type="submit" variant="primary" size="lg" loading={busy}>
          {t('auth.setPassword')}
        </Button>
        <button
          type="button"
          onClick={() => void signOut()}
          className="text-sm text-muted hover:underline"
        >
          {t('auth.signOut')}
        </button>
      </form>
    </AuthLayout>
  );
}
